/** Remove for one unstarted rate: the button, then an inline confirm. Focus
 *  moves to the confirm when it opens and back to Remove when it closes. */
import { useEffect, useRef, useState } from 'react';

import type { PayRateEntry } from '../../../api/staff-pay-rates';

import {
  formatCalendarDate,
  formatRateCents,
  formatShortDate,
  PAY_RATE_ROLE_LABELS,
} from './pay-rate-format';

const BUTTON = 'min-h-[24px] min-w-[24px] rounded px-2 text-xs';
const FOCUS = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-moss-500';

export default function PayRateRemoveControl({
  entry,
  busy,
  onRemove,
}: {
  entry: PayRateEntry;
  busy: boolean;
  onRemove: (rateId: string) => void;
}) {
  const [asking, setAsking] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const wasAsking = useRef(false);

  useEffect(() => {
    if (asking) confirmRef.current?.focus();
    else if (wasAsking.current) triggerRef.current?.focus();
    wasAsking.current = asking;
  }, [asking]);

  if (!asking) {
    return (
      <button
        ref={triggerRef}
        type="button"
        disabled={busy}
        onClick={() => setAsking(true)}
        aria-label={`Remove ${PAY_RATE_ROLE_LABELS[entry.role]} rate starting ${formatCalendarDate(entry.effectiveFrom)}`}
        className={`${BUTTON} ${FOCUS} text-red-700 dark:text-red-400 hover:underline disabled:opacity-50`}
      >
        Remove
      </button>
    );
  }
  return (
    <span
      className="flex items-center gap-2"
      onKeyDown={(e) => {
        if (e.key === 'Escape') setAsking(false);
      }}
    >
      <span className="text-stone-700 dark:text-stone-300">
        Remove {formatRateCents(entry.amountCents)} from {formatShortDate(entry.effectiveFrom)}?
      </span>
      <button
        ref={confirmRef}
        type="button"
        disabled={busy}
        onClick={() => {
          setAsking(false);
          onRemove(entry.id);
        }}
        className={`${BUTTON} ${FOCUS} bg-red-700 text-white hover:bg-red-800 disabled:opacity-50`}
      >
        Remove
      </button>
      <button
        type="button"
        onClick={() => setAsking(false)}
        className={`${BUTTON} ${FOCUS} text-stone-700 dark:text-stone-300 hover:underline`}
      >
        Keep
      </button>
    </span>
  );
}
